/**
 * Web-specific position multi-select using react-select with isMulti.
 * Provides native dropdown UX with search, keyboard navigation, and theme support.
 * @module components/ManuallyAddPlayerModal/PositionMultiSelect
 */

import { memo, useCallback, useMemo } from "react";
import Select, { GroupBase, StylesConfig } from "react-select";

import { PositionMultiSelectProps } from "@/types/manuallyAddPlayerModalTypes";
import { IOptionSelect } from "@/types/customSelectTypes";
import { RADIUS } from "@/config/radius";

/**
 * Creates themed styles for the react-select multi-select dropdown
 * @param isDark - Whether dark theme is active
 */
const createMultiSelectStyles = (
  isDark: boolean
): StylesConfig<IOptionSelect, true, GroupBase<IOptionSelect>> => {
  // Use Gluestack background-300 tokens to match the Input component border color:
  // light: rgb(213,212,212) = #D5D4D4 | dark: rgb(116,116,116) = #747474
  const borderColor = isDark ? "#747474" : "#D5D4D4";
  // Match Gluestack background-50 dark (#272625) and white for light
  const backgroundColor = isDark ? "#272625" : "#ffffff";
  const textColor = isDark ? "#F9FAFB" : "#111827";
  // Hover/selected use background-100 tokens: light #F6F6F6, dark #414040
  const hoverColor = isDark ? "#414040" : "#F6F6F6";
  const selectedColor = isDark ? "#414040" : "#F6F6F6";
  // Chips use background-100 tokens: light #F6F6F6, dark #414040
  const chipBg = isDark ? "#414040" : "#F6F6F6";

  return {
    control: base => ({
      ...base,
      backgroundColor,
      // Match Gluestack Input: rounded-sm (4px), h-12 (48px), 2px border, 16px text
      borderRadius: RADIUS.sm,
      border: `2px solid ${borderColor}`,
      minHeight: "48px",
      fontSize: "16px",
      boxShadow: "none",
      "&:hover": { borderColor },
    }),
    menu: base => ({
      ...base,
      backgroundColor,
      border: `1px solid ${borderColor}`,
      borderRadius: RADIUS.sm,
      zIndex: 9999,
    }),
    // Portal container also needs zIndex so the dropdown layers above modals
    // when menuPortalTarget is set (e.g. document.body)
    menuPortal: base => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected
        ? selectedColor
        : isFocused
          ? hoverColor
          : backgroundColor,
      color: textColor,
      cursor: "pointer",
      fontSize: "16px",
    }),
    multiValue: base => ({
      ...base,
      backgroundColor: chipBg,
      borderRadius: RADIUS.lg,
    }),
    multiValueLabel: base => ({
      ...base,
      color: textColor,
      fontSize: "13px",
      fontStyle: "normal",
    }),
    multiValueRemove: base => ({
      ...base,
      color: textColor,
      borderRadius: `${RADIUS.none}px ${RADIUS.lg}px ${RADIUS.lg}px ${RADIUS.none}px`,
      "&:hover": {
        backgroundColor: isDark ? "#747474" : "#D5D4D4",
        color: textColor,
      },
    }),
    placeholder: base => ({
      ...base,
      color: isDark ? "#747474" : "#9CA3AF",
      fontSize: "16px",
    }),
    input: base => ({
      ...base,
      color: textColor,
      fontSize: "16px",
    }),
    menuList: base => ({
      ...base,
    }),
  };
};

/**
 * Web position multi-select using react-select for optimal dropdown UX
 * @param props - Component props
 * @param props.options - Available position options
 * @param props.value - Currently selected position IDs
 * @param props.onChange - Callback when selection changes
 * @param props.isDark - Whether dark theme is active
 * @param props.isDisabled - Whether the field is disabled
 * @param props.testID - Test ID for the component
 */
const PositionMultiSelectView = ({
  options,
  value,
  onChange,
  isDark,
  isDisabled,
  testID,
}: PositionMultiSelectProps) => {
  const styles = useMemo(() => createMultiSelectStyles(isDark), [isDark]);

  const selectedOptions = useMemo(
    () => options.filter(opt => value.includes(opt.value)),
    [options, value]
  );

  const handleChange = useCallback(
    (selected: readonly IOptionSelect[]) => {
      onChange(selected.map(s => s.value));
    },
    [onChange]
  );

  return (
    <div data-testid={testID}>
      <Select
        options={[...options]}
        isMulti
        // Typeahead: typing filters the position list (SE-5095)
        isSearchable
        placeholder="Select positions"
        value={selectedOptions}
        onChange={handleChange}
        styles={styles}
        isDisabled={isDisabled}
        menuPortalTarget={document.body}
      />
    </div>
  );
};

PositionMultiSelectView.displayName = "PositionMultiSelectView";

export default memo(PositionMultiSelectView);
